import { Component } from '@theme/component';

/**
 * Animate controller — wrapper declarativo de animações com GSAP 3.13+ carregado
 * sob demanda (ESM via CDN jsDelivr). Componente de FUNDAÇÃO.
 *
 * Presets disponíveis:
 *  fade, fade-up, fade-down, fade-left, fade-right,
 *  scale (alias zoom), blur, clip, flip, rise, rotate,
 *  split-words, split-chars, split-lines, stagger-children.
 *
 * Triggers:
 *  scroll (default) — ScrollTrigger toggleActions, dispara uma vez (once: true).
 *  load             — anima ao conectar, sem ScrollTrigger.
 *  scrub            — progresso ligado ao scroll (sem once, usa data-end e data-scrub).
 *
 * Estratégia de performance (Lei IV):
 *  - GSAP não é importado estaticamente. IntersectionObserver (200px rootMargin) dispara
 *    o dynamic import só quando o componente chega perto do viewport.
 *  - Loader singleton: gsapPromise cacheada em escopo de módulo (1 download por página).
 *  - Versão travada em @3.13.0 (ScrollTrigger e SplitText gratuitos a partir do 3.13).
 *
 * Anti-FOUC:
 *  - `.animate--armed` (opacity 0) aplicada no connectedCallback antes do GSAP chegar.
 *  - Para 'clip': `.animate--armed-clip` (clipPath inset) ao invés de opacity.
 *  - Para 'scrub': sem arming (estado parcial deve ser visível).
 *  - Se GSAP falhar, IntersectionObserver faltar, ou prefers-reduced-motion, o armed é
 *    revertido imediatamente e o conteúdo fica 100% visível.
 *
 * Nota sobre animações usando fromTo():
 *  Os presets que usam gsap.fromTo() (clip, flip, rise, rotate, stagger-children, scrub)
 *  definem TO explícito (opacity: 1, clearProps) — evitando o problema de leitura do
 *  estado CSS armado como destino.
 *
 * @extends {Component}
 */
export class AnimateComponent extends Component {
  static GSAP_VERSION = '3.13.0';
  static GSAP_JS = `https://cdn.jsdelivr.net/npm/gsap@${AnimateComponent.GSAP_VERSION}/+esm`;
  static SCROLLTRIGGER_JS = `https://cdn.jsdelivr.net/npm/gsap@${AnimateComponent.GSAP_VERSION}/ScrollTrigger/+esm`;
  static SPLITTEXT_JS = `https://cdn.jsdelivr.net/npm/gsap@${AnimateComponent.GSAP_VERSION}/SplitText/+esm`;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {{ kill: () => void } | null} */
  #tween = null;

  /** @type {Array<{ kill: () => void }>} */
  #scrollTriggers = [];

  /** @type {{ revert: () => void } | null} */
  #split = null;

  #booting = false;
  #armed = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('shopify:section:unload', this.#handleSectionUnload);

    const prefersReduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced || !('IntersectionObserver' in window)) {
      this.#disarm();
      return;
    }

    const trigger = this.dataset.trigger || 'scroll';

    // scrub: não arma (o estado parcial deve ficar visível conforme o scroll)
    if (trigger !== 'scrub') {
      this.#arm();
    }

    if (trigger === 'load') {
      this.#boot();
      return;
    }

    // scroll e scrub: espera o elemento se aproximar do viewport
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#disconnectObserver();
            this.#boot();
            break;
          }
        }
      },
      { rootMargin: '200px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#disconnectObserver();
    document.removeEventListener('shopify:section:unload', this.#handleSectionUnload);
    this.#destroy();
    this.#disarm();
  }

  async #boot() {
    if (this.#booting || this.#tween) return;
    this.#booting = true;

    try {
      const { gsap, ScrollTrigger, SplitText } = await loadGsap();

      if (!this.isConnected) return;

      this.#animate(gsap, ScrollTrigger, SplitText);
    } catch (error) {
      console.warn('[animate] Failed to load GSAP via CDN; content revealed without animation.', error);
      this.#disarm();
    } finally {
      this.#booting = false;
    }
  }

  /**
   * @param {any} gsap
   * @param {any} _ScrollTrigger
   * @param {any} SplitText
   */
  #animate(gsap, _ScrollTrigger, SplitText) {
    const ds = this.dataset;
    const n = AnimateComponent.#toNumber;

    const animation = ds.animation || 'fade';
    const trigger = ds.trigger || 'scroll';
    const delay = n(ds.delay, 0);
    const duration = n(ds.duration, 0.6);
    const ease = ds.ease || 'power2.out';
    const stagger = n(ds.stagger, 0.04);
    const distance = n(ds.distance, 24);
    const start = ds.start || 'top 85%';
    const once = ds.once !== 'false';

    this.#disarm();

    this.style.willChange = 'opacity, transform, filter';

    // — Scrub: progresso ligado ao scroll —
    if (trigger === 'scrub') {
      const end = ds.end || 'bottom top';
      const scrubSmooth = n(ds.scrub, 1);
      const fromVars = AnimateComponent.#presetFrom(animation, distance);
      const toVars = AnimateComponent.#presetTo(animation);

      this.#tween = gsap.fromTo(this, fromVars, {
        ...toVars,
        ease: 'none',
        scrollTrigger: { trigger: this, start, end, scrub: scrubSmooth },
        onComplete: () => this.#onComplete(),
      });
      this.#captureScrollTrigger();
      return;
    }

    /** @type {Record<string, unknown>} */
    const common = { duration, ease, delay };
    if (trigger !== 'load') {
      common.scrollTrigger = { trigger: this, start, once };
    }

    // — Split text —
    if (['split-words', 'split-chars', 'split-lines'].includes(animation)) {
      this.#tween = this.#animateSplit(gsap, SplitText, animation, { common, stagger, distance });
      this.#captureScrollTrigger();
      return;
    }

    // — Stagger children —
    if (animation === 'stagger-children') {
      const children = Array.from(this.children);
      if (!children.length) {
        this.#disarm();
        return;
      }
      const fromVars = AnimateComponent.#presetFrom('fade-up', distance);
      const toVars = AnimateComponent.#presetTo('fade-up');
      this.#tween = gsap.fromTo(children, fromVars, {
        ...toVars,
        ...common,
        stagger,
        onComplete: () => this.#onComplete(),
      });
      this.#captureScrollTrigger();
      return;
    }

    // — Presets fromTo (clip, flip, rise, rotate): TO explícito para opacity animar corretamente —
    const fromToPresets = ['clip', 'flip', 'rise', 'rotate'];
    if (fromToPresets.includes(animation)) {
      const fromVars = AnimateComponent.#presetFrom(animation, distance);
      const toVars = AnimateComponent.#presetTo(animation);
      const clearPropsValue = animation === 'clip' ? 'clipPath,opacity' : 'opacity';
      this.#tween = gsap.fromTo(this, fromVars, {
        ...toVars,
        ...common,
        clearProps: clearPropsValue,
        onComplete: () => this.#onComplete(),
      });
      this.#captureScrollTrigger();
      return;
    }

    // — Presets clássicos (from) —
    const fromVars = AnimateComponent.#presetFrom(animation, distance);
    this.#tween = gsap.from(this, {
      ...fromVars,
      ...common,
      clearProps: 'filter',
      onComplete: () => this.#onComplete(),
    });
    this.#captureScrollTrigger();
  }

  /**
   * @param {any} gsap
   * @param {any} SplitText
   * @param {string} animation
   * @param {{ common: Record<string, unknown>, stagger: number, distance: number }} opts
   */
  #animateSplit(gsap, SplitText, animation, { common, stagger, distance }) {
    const type = animation === 'split-chars' ? 'chars' : animation === 'split-lines' ? 'lines' : 'words';
    this.#split = new SplitText(this, { type });
    const targets = this.#split[type];

    return gsap.from(targets, {
      opacity: 0,
      y: Math.min(distance, 16),
      ...common,
      stagger,
      onComplete: () => {
        this.#split?.revert();
        this.#split = null;
        this.#onComplete();
      },
    });
  }

  #captureScrollTrigger() {
    const st = /** @type {any} */ (this.#tween)?.scrollTrigger;
    if (st) this.#scrollTriggers.push(st);
  }

  #onComplete() {
    this.style.willChange = '';
    this.#disarm();
  }

  #destroy() {
    for (const st of this.#scrollTriggers) st.kill();
    this.#scrollTriggers = [];
    this.#split?.revert();
    this.#split = null;
    this.#tween?.kill();
    this.#tween = null;
    this.style.willChange = '';
  }

  #arm() {
    if (this.#armed) return;
    const animation = this.dataset.animation || 'fade';
    // clip usa clipPath para esconder — não precisa de opacity
    if (animation === 'clip') {
      this.classList.add('animate--armed-clip');
    } else {
      this.classList.add('animate--armed');
    }
    this.#armed = true;
  }

  #disarm() {
    this.classList.remove('animate--armed', 'animate--armed-clip');
    this.#armed = false;
  }

  #disconnectObserver() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #handleSectionUnload = (event) => {
    const target = event.target;
    if (target instanceof Node && target.contains(this)) {
      this.#destroy();
      this.#disarm();
    }
  };

  /**
   * Vars iniciais para gsap.from() e fromTo().
   * @param {string} animation
   * @param {number} distance
   * @returns {Record<string, unknown>}
   */
  static #presetFrom(animation, distance) {
    switch (animation) {
      case 'fade':
        return { opacity: 0 };
      case 'fade-up':
        return { opacity: 0, y: distance };
      case 'fade-down':
        return { opacity: 0, y: -distance };
      case 'fade-left':
        return { opacity: 0, x: distance };
      case 'fade-right':
        return { opacity: 0, x: -distance };
      case 'scale':
      case 'zoom':
        return { opacity: 0, scale: 0.92 };
      case 'blur':
        return { opacity: 0, filter: 'blur(8px)' };
      // fromTo presets — opacity explícita no TO (#presetTo)
      case 'clip':
        return { clipPath: 'inset(100% 0 0 0)', opacity: 0 };
      case 'flip':
        return { rotationX: -90, transformOrigin: '50% 100%', opacity: 0 };
      case 'rise':
        return { opacity: 0, scale: 0.88, y: distance * 0.6 };
      case 'rotate':
        return { opacity: 0, rotation: -8, y: distance * 0.5, transformOrigin: '50% 100%' };
      default:
        return { opacity: 0, y: distance };
    }
  }

  /**
   * Vars finais explícitas para gsap.fromTo() (presets que precisam de opacity: 1 definida).
   * @param {string} animation
   * @returns {Record<string, unknown>}
   */
  static #presetTo(animation) {
    switch (animation) {
      case 'fade':
        return { opacity: 1 };
      case 'fade-up':
      case 'fade-down':
        return { opacity: 1, y: 0 };
      case 'fade-left':
      case 'fade-right':
        return { opacity: 1, x: 0 };
      case 'scale':
      case 'zoom':
        return { opacity: 1, scale: 1 };
      case 'blur':
        return { opacity: 1, filter: 'blur(0px)' };
      case 'clip':
        return { clipPath: 'inset(0% 0 0 0)', opacity: 1 };
      case 'flip':
        return { rotationX: 0, opacity: 1 };
      case 'rise':
        return { opacity: 1, scale: 1, y: 0 };
      case 'rotate':
        return { opacity: 1, rotation: 0, y: 0 };
      default:
        return { opacity: 1, y: 0 };
    }
  }

  restart() {
    this.#disconnectObserver();
    this.#destroy();
    this.#disarm();

    const trigger = this.dataset.trigger || 'scroll';

    if (trigger !== 'scrub') {
      this.#arm();
    }

    if (trigger === 'load') {
      this.#boot();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#disconnectObserver();
            this.#boot();
            break;
          }
        }
      },
      { rootMargin: '200px' }
    );
    this.#observer.observe(this);
  }

  static #toNumber(value, fallback) {
    const n = Number(value);
    return Number.isFinite(n) && value !== undefined && value !== '' ? n : fallback;
  }
}

/**
 * Loader singleton do GSAP (carrega UMA vez por página).
 * @type {Promise<{ gsap: any, ScrollTrigger: any, SplitText: any }> | null}
 */
let gsapPromise = null;

function loadGsap() {
  if (gsapPromise) return gsapPromise;

  gsapPromise = (async () => {
    const [gsapMod, scrollTriggerMod, splitTextMod] = await Promise.all([
      import(AnimateComponent.GSAP_JS),
      import(AnimateComponent.SCROLLTRIGGER_JS),
      import(AnimateComponent.SPLITTEXT_JS),
    ]);

    const gsap = gsapMod.gsap ?? gsapMod.default;
    const ScrollTrigger = scrollTriggerMod.ScrollTrigger ?? scrollTriggerMod.default;
    const SplitText = splitTextMod.SplitText ?? splitTextMod.default;

    gsap.registerPlugin(ScrollTrigger, SplitText);
    return { gsap, ScrollTrigger, SplitText };
  })();

  gsapPromise.catch(() => {
    gsapPromise = null;
  });

  return gsapPromise;
}

if (!customElements.get('animate-component')) {
  customElements.define('animate-component', AnimateComponent);
}
